import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { DNSZone, DNSRecord } from '../../types/dns';

// Modular components
import ZoneHeader from './components/ZoneHeader';
import ZoneOverview from './components/ZoneOverview';
import ZoneNameservers from './components/ZoneNameservers';
import DNSRecordsList from './components/DNSRecordsList';
import BulkDeleteModal from './components/BulkDeleteModal';
import { DNSSECCard, EmailRoutingCard } from './components/ZoneFeatures';

interface ZoneDetailsPanelProps {
  selectedZone: DNSZone | null;
  zoneRecords: DNSRecord[];
  recordsLoading?: boolean;
  recordsError?: string | null;
  onRefreshRecords: (zoneId: string) => void;
  onEditRecord?: (record: DNSRecord) => void;
  onDeleteRecord?: (recordId: string) => void;
  onAddRecord?: () => void;
  onBulkDelete?: (recordIds: string[]) => Promise<void>;
  onBulkUpdateTTL?: (recordIds: string[], ttl: number) => Promise<void>;
}

const ZoneDetailsPanel: React.FC<ZoneDetailsPanelProps> = ({
  selectedZone,
  zoneRecords,
  recordsLoading = false,
  recordsError = null,
  onRefreshRecords,
  onEditRecord,
  onDeleteRecord,
  onAddRecord,
  onBulkDelete,
  onBulkUpdateTTL,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [bulkTTL, setBulkTTL] = useState<string>('300');

  // Reset selection on zone change
  useEffect(() => {
    setSelectedIds([]);
    setConfirmText('');
    setConfirmBulkDelete(false);
  }, [selectedZone?.id]);

  const handleRefresh = async () => {
    if (!selectedZone) return;
    setIsRefreshing(true);
    try {
      await onRefreshRecords(selectedZone.id);
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = zoneRecords.length > 0 && selectedIds.length === zoneRecords.length;
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(zoneRecords.map((r) => r.id));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || !selectedZone) return;
    try {
      await onBulkDelete(selectedIds);
      setSelectedIds([]);
      setConfirmText('');
      setConfirmBulkDelete(false);
    } catch (e) {
      console.error('Bulk delete failed:', e);
    }
  };

  const handleBulkTTLUpdate = async () => {
    const ttlNum = parseInt(bulkTTL, 10);
    if (!selectedZone || !onBulkUpdateTTL || !ttlNum || selectedIds.length === 0) return;
    try {
      await onBulkUpdateTTL(selectedIds, ttlNum);
      setSelectedIds([]);
    } catch (e) {
      console.error('Bulk TTL update failed:', e);
    }
  };

  return (
    <AnimatePresence>
      {selectedZone && (
        <motion.div
          className="xl:col-span-2 2xl:col-span-1"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="card sticky top-6">
            <ZoneHeader 
              zone={selectedZone} 
              isRefreshing={isRefreshing} 
              onRefresh={handleRefresh} 
            />

            <div className="card-body space-y-6">
              <ZoneOverview zone={selectedZone} recordCount={zoneRecords.length} />
              <ZoneNameservers nameservers={selectedZone.nameservers} />
              <DNSSECCard zone={selectedZone} />
              <EmailRoutingCard zone={selectedZone} />

              <DNSRecordsList
                zone={selectedZone}
                records={zoneRecords}
                loading={recordsLoading}
                error={recordsError}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onEditRecord={onEditRecord}
                onAddRecord={onAddRecord}
                onRefresh={onRefreshRecords}
                bulkTTL={bulkTTL}
                setBulkTTL={setBulkTTL}
                onBulkTTLUpdate={handleBulkTTLUpdate}
                onBulkDeleteClick={() => setConfirmBulkDelete(true)}
              />
            </div>
          </div>

          <BulkDeleteModal
            isOpen={confirmBulkDelete}
            selectedCount={selectedIds.length}
            zone={selectedZone}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            onConfirm={handleBulkDelete}
            onCancel={() => {
              setConfirmText('');
              setConfirmBulkDelete(false);
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ZoneDetailsPanel;
